import { Link } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { ArrowPathIcon, ArrowRightIcon, CreditCardIcon, TrashIcon } from '@heroicons/react/24/outline';

import { SubPageHeader } from '@/components/shared/SubPageHeader/SubPageHeader';
import { deleteFoodBenefitImportById, listFoodBenefitImports } from '../services/foodBenefitImportsService';
import type { FoodBenefitImportDocument } from '../services/foodBenefitTypes';

type FoodBenefitImportRow = {
  id: string;
  competence: string;
  totalAmount: number;
  totalOrders: number;
  collaboratorCount: number;
  createdAtLabel: string;
};

export function FoodBenefitImportsPage() {
  const [imports, setImports] = useState<FoodBenefitImportDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const fetchImports = async () => {
      setLoading(true);
      try {
        const data = await listFoodBenefitImports();
        if (!active) return;
        setImports(data);
        setLoadError(null);
      } catch (error) {
        console.error('[FoodBenefitImportsPage] failed to load imports', error);
        if (!active) return;
        setLoadError('Não foi possível carregar as importações.');
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };
    void fetchImports();
    return () => {
      active = false;
    };
  }, []);

  const formattedImports = useMemo<FoodBenefitImportRow[]>(() => {
    return imports
      .map((item) => {
        const collaboratorIds = new Set<string>();
        (item.recordsPreview ?? []).forEach((record: any) => {
          const collaboratorId = typeof record?.collaboratorId === 'string' ? record.collaboratorId : '';
          if (collaboratorId) collaboratorIds.add(collaboratorId);
        });
        return {
          id: item.id,
          competence: item.billingPeriod ?? 'Sem competência',
          totalAmount: item.totals?.amount ?? 0,
          totalOrders: item.orders?.length ?? item.totals?.feeCount ?? item.recordsPreview?.length ?? 0,
          collaboratorCount: collaboratorIds.size,
          createdAtLabel: item.createdAt
            ? item.createdAt.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
            : '—',
        };
      })
      .sort((a, b) => {
        if (!a.competence) return 1;
        if (!b.competence) return -1;
        return b.competence.localeCompare(a.competence);
      });
  }, [imports]);

  const handleRemoveImport = async (importId: string, competence: string | null) => {
    const label = competence ? ` (${competence})` : '';
    if (!window.confirm(`Remover a importação${label}? Esta ação não pode ser desfeita.`)) return;
    try {
      await deleteFoodBenefitImportById(importId);
      setImports((prev) => prev.filter((item) => item.id !== importId));
    } catch (error) {
      console.error('[FoodBenefitImportsPage] failed to delete import', error);
      setLoadError('Não foi possível remover a importação.');
    }
  };

  const formatCurrency = (value: number): string =>
    value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  return (
    <div className="space-y-6">
      <SubPageHeader
        backTo="/expenses/benefits"
        icon={<CreditCardIcon className="h-5 w-5" aria-hidden />}
        badge="Benefícios"
        title="Auxílio alimentação"
        description="Acompanhe importações, totais por competência e pedidos vinculados."
        actions={
          <Link
            to="/expenses/benefits/food/import"
            className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-primary-700"
          >
            Importar
            <ArrowRightIcon className="h-4 w-4" aria-hidden />
          </Link>
        }
      />

      <div className="rounded-lg border border-primary-100 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Importações</h2>
            <p className="text-sm text-gray-600">Visualize e gerencie pedidos importados por competência.</p>
          </div>
          {loading ? <ArrowPathIcon className="h-5 w-5 animate-spin text-gray-500" aria-hidden /> : null}
        </div>

        {loadError ? (
          <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">{loadError}</div>
        ) : null}

        {!loading && formattedImports.length === 0 && !loadError ? (
          <div className="mt-4 text-sm text-gray-600">Nenhuma importação registrada.</div>
        ) : null}

        {formattedImports.length > 0 ? (
          <div className="mt-4 overflow-auto rounded-md border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Competência</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Valor total</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Pedidos</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Colaboradores vinculados</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Criado em</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {formattedImports.map((row) => (
                  <tr key={row.id}>
                    <td className="px-3 py-2 text-gray-900">
                      <Link
                        to={`/expenses/benefits/food/imports/${encodeURIComponent(row.competence)}`}
                        className="text-primary-700 hover:text-primary-800"
                      >
                        {row.competence}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-gray-900">{formatCurrency(row.totalAmount)}</td>
                    <td className="px-3 py-2 text-gray-900">{row.totalOrders}</td>
                    <td className="px-3 py-2 text-gray-900">{row.collaboratorCount}</td>
                    <td className="px-3 py-2 text-gray-900">{row.createdAtLabel}</td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => handleRemoveImport(row.id, row.competence)}
                        className="inline-flex items-center gap-1 rounded-md border border-red-200 px-2 py-1 text-xs font-semibold text-red-700 transition hover:-translate-y-0.5 hover:border-red-300"
                      >
                        <TrashIcon className="h-4 w-4" aria-hidden />
                        Remover
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </div>
  );
}
